/**
 * The 0-100% slider, extracted from `PerpsTradeForm/form.LeverageSelectInput`.
 *
 * It is `react-slider` rather than a div with a mousedown handler for one
 * reason: the WAI-ARIA slider pattern comes with it — `role="slider"`,
 * `aria-valuenow/min/max`, and the full keyboard (arrows a step, PageUp/Down
 * ten, Home and End the ends). The hand-rolled tracks this replaces were
 * mouse-only, so a keyboard trader could set a size but not a percentage of it.
 *
 * `onChange` fires as the thumb moves — that is what the field echoes — and
 * `onCommit` fires once, when the drag or the key ends: the moment a caller
 * should re-quote or re-validate, rather than sixty times on the way there.
 */
import { useEffect, useState } from "react";
import ReactSlider from "react-slider";
import styled from "styled-components";
import {
  COLORS,
  LAYER_COLORS,
  PRIMARY_COLORS,
} from "../../../constants/design/colors";
import { percentSpec } from "../../../utils/amount/spec";
import { BORDER_RADIUS, SPACING } from "../../../constants/design/spacing";
import { NumericInput } from "./NumericInput";
import { SlotLabel } from "./style";

/** Half the thumb, so a tick at 100% sits under the thumb's centre. */
const THUMB = 14;
const TRACK_INSET = THUMB / 2;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  width: 100%;
`;

const SliderLane = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  height: ${SPACING.four + SPACING.twoHalf}px;
`;

const StyledSlider = styled(ReactSlider)`
  flex: 1;
  display: flex;
  align-items: center;
  z-index: 1;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const StyledThumb = styled.div<{ $disabled?: boolean }>`
  width: ${THUMB}px;
  height: ${THUMB}px;
  border-radius: 50%;
  border: 2px solid
    ${({ $disabled }) => ($disabled ? LAYER_COLORS.four : PRIMARY_COLORS.one)};
  background-color: ${LAYER_COLORS.one};
  top: 50% !important;
  transform: translateY(-50%);
  flex-shrink: 0;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "grab")};

  &:active {
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "grabbing")};
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px ${PRIMARY_COLORS.four};
  }
`;

const StyledTrack = styled.div<{ index: number; $disabled?: boolean }>`
  height: 2px;
  border-radius: ${BORDER_RADIUS.full}px;
  background: ${({ index, $disabled }) =>
    index === 0 && !$disabled ? PRIMARY_COLORS.one : LAYER_COLORS.three};
`;

const TickLane = styled.div`
  position: absolute;
  left: ${TRACK_INSET}px;
  right: ${TRACK_INSET}px;
  top: 0;
  bottom: 0;
  pointer-events: none;
`;

const Tick = styled.span<{ $at: number }>`
  position: absolute;
  top: 50%;
  left: ${({ $at }) => $at}%;
  width: 5px;
  height: 5px;
  margin: -2.5px 0 0 -2.5px;
  border-radius: 50%;
  background-color: ${COLORS.white.three};
`;

const PercentBox = styled.div`
  flex: 0 0 auto;
  width: 76px;
`;

export interface PercentSliderProps {
  /** 0-100. Anything outside is taken as the nearest end. */
  value: number;
  /** Fires as the thumb moves. */
  onChange: (pct: number) => void;
  /** Fires once the drag or the key ends. */
  onCommit?: (pct: number) => void;
  disabled?: boolean;
  /** Required: "50" means nothing to a screen reader without it. */
  ariaLabel: string;
  /** Where the tick dots sit. */
  presets?: number[];
  /** The 0-100 box beside the track. */
  showInput?: boolean;
}

const clampPercent = (pct: number): number => {
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

const spec = percentSpec();

export const PercentSlider = ({
  value,
  onChange,
  onCommit,
  disabled,
  ariaLabel,
  presets = [25, 50, 75, 100],
  showInput = true,
}: PercentSliderProps) => {
  const percent = clampPercent(value);
  const [text, setText] = useState(String(percent));

  // The box follows the thumb. It does not fight what is being typed: only a
  // percentage that actually changed reaches it.
  useEffect(() => {
    setText(String(percent));
  }, [percent]);

  const commitText = (raw: string) => {
    const next = clampPercent(Number(raw));
    setText(String(next));
    onChange(next);
    onCommit?.(next);
  };

  return (
    <SliderRow>
      <SliderLane>
        <StyledSlider
          value={percent}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          ariaLabel={ariaLabel}
          ariaValuetext={(state: any) => `${state.valueNow}%`}
          // react-slider builds these prop objects with `key` INSIDE them, so
          // spreading them passes a key through a spread: React 18 warns and
          // React 19 drops it, losing the identity of the thumb and track.
          renderThumb={({ key, ...thumbProps }: any) => (
            <StyledThumb key={key} {...thumbProps} $disabled={disabled} />
          )}
          renderTrack={({ key, ...trackProps }: any, { index }: any) => (
            <StyledTrack
              key={key}
              {...trackProps}
              index={index}
              $disabled={disabled}
            />
          )}
          onChange={(next: any) => {
            if (typeof next === "number") {
              setText(String(next));
              onChange(next);
            }
          }}
          onAfterChange={(next: any) => {
            if (typeof next === "number") onCommit?.(next);
          }}
        />
        <TickLane aria-hidden="true">
          {presets.map((pct) => (
            <Tick key={pct} $at={clampPercent(pct)} data-testid="percent-slider-tick" />
          ))}
        </TickLane>
      </SliderLane>

      {showInput ? (
        <PercentBox>
          <NumericInput
            value={text}
            spec={spec}
            disabled={disabled}
            ariaLabel={ariaLabel}
            unitSlot={<SlotLabel>%</SlotLabel>}
            onValueChange={setText}
            // Commit on blur / Enter only: a half-typed "8" on the way to "80"
            // must not resize the order.
            onFinalize={commitText}
          />
        </PercentBox>
      ) : null}
    </SliderRow>
  );
};
